/**
 * FenzSafe "Find related files" discovery -- Slice 1A (Issue #114 founder product requirement,
 * comment 5726376684; feasibility contract at docs/ai/FENZSAFE_DISCOVERY_FEASIBILITY_CONTRACT.md;
 * scoped per the founder's review/corrections at comments 5727903903, 5728673302, 5729176495, and
 * 5730113935).
 *
 * Deliberately dependency-light per those reviews: browser-selected-folder discovery only (no
 * desktop companion); filename/path matching against the matter's own name/reference/existing
 * document names; direct text-content matching for TXT/CSV, PDF (text layer only, via
 * `pdfjs-dist`), EML (text/header only, via `postal-mime`), and DOCX (text only, via `mammoth`,
 * gated on the ZIP archive preflight in fenzsafeArchivePreflight.ts passing first) -- all loaded
 * via dynamic `import()` only when that file type is actually encountered mid-scan, never part of
 * the main bundle; and a plain date-window signal built only from the matter's own already-real
 * timestamps. XLSX/MSG/OCR/general-archive extraction remain deferred/out of scope -- XLSX
 * specifically because the archive preflight alone does not resolve its still-unverified read-path
 * dependency risk (feasibility contract §12). This is local, deterministic keyword/substring/
 * date-range scoring, not an AI or semantic model -- it must never be labeled either, per the
 * founder's explicit correction.
 *
 * Everything here runs entirely client-side (contract §2's local-vs-server boundary): the picked
 * directory handle, every file's bytes, and every match reason never leave this browser tab. No
 * network call is made by any function in this module -- `pdfjs-dist`'s own document loading is
 * configured with `disableAutoFetch`/`disableStream` so it never opportunistically fetches more
 * of a file than a one-shot text extraction needs, `postal-mime` performs no network I/O of its
 * own at all, and `mammoth.extractRawText()` has no options surface that could enable following
 * external relationships or embedding images. No absolute local filesystem path is ever produced
 * -- `DiscoveryCandidate.path` is always relative to the user-picked root.
 */

import {extractDocxRawText} from './fenzsafeDocxText';
import {emlSearchText, extractEmlPlainText, MAX_EML_PARSE_BYTES} from './fenzsafeEmlText';

export type DiscoveryMatchReason = {
  kind: 'reference' | 'name_keyword' | 'duplicate_name' | 'text_content' | 'date_window';
  detail: string;
};

export type DiscoveryCandidate = {
  /** Stable within one scan (derived from the relative path) -- used as the React list key and the selection-set key, never sent anywhere. */
  id: string;
  name: string;
  /** Relative to the picked root only -- never the file's real absolute path on disk, and never transmitted (contract §5: "show the exact granted path/scope," not the full filesystem path). */
  path: string;
  sizeBytes: number;
  lastModifiedAt: number;
  reasons: DiscoveryMatchReason[];
  band: 'strong' | 'possible';
  /** The real, already-authorized File handle -- kept only in memory for this scan, needed by a later import slice. */
  file: File;
};

export type DiscoveryMatterProfile = {
  name: string;
  reference: string;
  existingDocumentNames: string[];
  /**
   * Every real, already-recorded timestamp this matter has (its own creation time plus each
   * existing document's added-at time), as whatever string the backend sent -- never a field
   * invented for this feature. The real FenzSafe API sends a parseable timestamp for these
   * (`Y-m-d H:i:s.u`); this local demo/fixture store instead uses human display strings like
   * "Sept 8, 2026 · 6:40 PM ET" that do not parse, so date-window matching (below) naturally
   * produces no matches at all in this local build -- an honest consequence of not having a real
   * date signal locally, not a bug, and it activates automatically once real timestamps flow in.
   */
  knownDates: string[];
};

/**
 * Chromium-only (contract §1) -- Firefox/Safari/mobile browsers do not implement
 * `window.showDirectoryPicker()` at all. Callers must check this before offering the "Find
 * related files" action and show a plain fallback message otherwise; this function is the single
 * source of truth for that decision, so the fallback UI and the actual scan can never disagree
 * about whether the API is available.
 */
export function isDirectoryDiscoverySupported(): boolean {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

const STOPWORDS = new Set(['the', 'a', 'an', 'of', 'and', 'or', 'vs', 'v', 'in', 'on', 'for', 'to', 'at']);

/**
 * Drops short/common words so "v" in "Doe v. Roe" or "the" in a matter name never becomes a
 * noisy, meaningless match. Exported (Issue #114 comment 5732455193, Desktop Discovery Phase B1)
 * only so `apps/fenzsafe-desktop-prototype`'s own parity test can assert its duplicated TXT/CSV
 * matcher produces identical output against the same shared fixtures -- this is a pure,
 * behavior-unchanged visibility widening, not a functional change to the browser discovery slice.
 */
export function keywordsFrom(text: string): string[] {
  const words = text.toLowerCase().split(/[^a-z0-9]+/).filter((word) => word.length >= 3 && !STOPWORDS.has(word));
  return Array.from(new Set(words));
}

/**
 * Pure, synchronous, and independently testable from any real filesystem access -- the actual
 * matching decision a real scan makes, isolated from directory traversal so it can be proven
 * correct without mocking the File System Access API at all.
 */
export function matchCandidate(fileName: string, matter: DiscoveryMatterProfile): {reasons: DiscoveryMatchReason[]; band: 'strong' | 'possible'} | null {
  const lowerFileName = fileName.toLowerCase();
  const reasons: DiscoveryMatchReason[] = [];

  const reference = matter.reference.trim();
  if (reference !== '' && lowerFileName.includes(reference.toLowerCase())) {
    reasons.push({kind: 'reference', detail: `Filename contains the matter reference "${reference}"`});
  }

  for (const keyword of keywordsFrom(matter.name)) {
    if (lowerFileName.includes(keyword)) {
      reasons.push({kind: 'name_keyword', detail: `Filename mentions "${keyword}"`});
    }
  }

  if (matter.existingDocumentNames.some((name) => name.toLowerCase() === lowerFileName)) {
    reasons.push({kind: 'duplicate_name', detail: 'Same name as a document already in this matter'});
  }

  if (reasons.length === 0) {
    return null;
  }

  return {reasons, band: bandFor(reasons)};
}

/** A reference hit alone is decisive; otherwise two or more independent reasons are needed to call a match "strong" rather than merely "possible." */
function bandFor(reasons: DiscoveryMatchReason[]): 'strong' | 'possible' {
  return reasons.some((reason) => reason.kind === 'reference') || reasons.length >= 2 ? 'strong' : 'possible';
}

export type DateWindow = {start: number; end: number};

/** Padding applied to both ends of the matter's own known-dates span, so a matter with only one recorded date (e.g. a brand-new matter with a single document) still gets a usable window instead of a single-instant one that would almost never match a real file. */
const DATE_WINDOW_PADDING_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Built only from the matter's own already-real timestamps (its creation time, its existing
 * documents' added-at times) -- never an invented "case date" or "hearing date" field this
 * codebase does not have. Returns null when none of those strings parse to a real date (this
 * local demo/fixture store's display-string dates never will -- see DiscoveryMatterProfile's own
 * doc comment), and a null window means date-window matching contributes nothing for that scan,
 * not a fabricated one.
 */
export function matterDateWindow(matter: DiscoveryMatterProfile): DateWindow | null {
  const parsed = matter.knownDates.map((value) => Date.parse(value)).filter((value) => !Number.isNaN(value));
  if (parsed.length === 0) {
    return null;
  }
  return {start: Math.min(...parsed) - DATE_WINDOW_PADDING_MS, end: Math.max(...parsed) + DATE_WINDOW_PADDING_MS};
}

/**
 * Plain-language only, per founder review comment 5729176495: a date-window match is a mundane
 * "this file's date lines up with this matter's other activity," never dressed up as legal
 * significance or an AI inference. Exported for the same Phase B1 parity-testing reason as
 * `keywordsFrom` above -- no behavior change.
 */
export function dateWindowReason(lastModifiedAt: number, window: DateWindow): DiscoveryMatchReason | null {
  if (lastModifiedAt < window.start || lastModifiedAt > window.end) {
    return null;
  }
  return {kind: 'date_window', detail: "File date falls within this matter's active date range"};
}

/** Extensions cheap and safe to read as plain text for the dependency-light first slice (per founder review comment 5727903903 on Issue #114: "at minimum TXT/CSV" -- no PDF/DOCX/XLSX/MSG/OCR/archive parser in this sub-slice). */
const TEXT_SCAN_EXTENSIONS = ['.txt', '.csv'];

function isTextScanCandidate(fileName: string): boolean {
  const lowerName = fileName.toLowerCase();
  return TEXT_SCAN_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
}

/** Per-file cap on how much of a single TXT/CSV file's content is scanned -- an "individual file size" scan limit (founder review comment 5727903903) distinct from the file-count limit below, so one huge log dropped in the folder can't freeze the UI. */
export const MAX_TEXT_SCAN_BYTES = 2_000_000;

/** Cumulative cap across the whole scan on how many content bytes get read -- a "total work" scan limit (founder review comment 5727903903), independent of the per-file and file-count limits, so many medium-sized text files can't add up to the same freeze risk. */
export const MAX_TOTAL_TEXT_SCAN_BYTES = 20_000_000;

/** `File.text()` is a real, standard Blob method every browser this feature supports implements, but `FileReader` is used instead -- it is the one text-reading API this codebase can rely on in every environment it actually runs (including its own test suite), with no behavioral difference for a real user. */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
    reader.readAsText(file);
  });
}

/** Same reasoning as readFileAsText() above -- `File.arrayBuffer()` is real and standard, but this project's own jsdom test environment doesn't implement it either, so `FileReader.readAsArrayBuffer()` is used for the same reason: the one API guaranteed to work everywhere this code actually runs, with no behavioral difference for a real user. */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result instanceof ArrayBuffer ? reader.result : new ArrayBuffer(0));
    reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * The same reference/keyword search matchCandidate() runs against a filename, run instead against
 * a document's extracted content (from any format) -- one shared rule for what counts as a "text
 * match," whatever produced the text. Exported for the same Phase B1 parity-testing reason as
 * `keywordsFrom` above -- no behavior change.
 */
export function contentTextReasons(content: string, matter: DiscoveryMatterProfile): DiscoveryMatchReason[] {
  const lower = content.toLowerCase();
  const reasons: DiscoveryMatchReason[] = [];

  const reference = matter.reference.trim();
  if (reference !== '' && lower.includes(reference.toLowerCase())) {
    reasons.push({kind: 'text_content', detail: `File contents mention the matter reference "${reference}"`});
  }

  for (const keyword of keywordsFrom(matter.name)) {
    if (lower.includes(keyword)) {
      reasons.push({kind: 'text_content', detail: `File contents mention "${keyword}"`});
    }
  }

  return reasons;
}

/** Reads a TXT/CSV file's own text and checks it for the same reference/keyword signals matchCandidate() checks in the filename -- a genuinely separate "text match" reason, not a name-match relabeled. Silently skips a file above the per-file or the scan's remaining cumulative budget; neither is something a legal user reading the results needs to reason about. Never throws -- a file this codebase can't read for any reason (permission revoked mid-scan, an I/O error) contributes no reason rather than aborting the whole scan. */
async function textContentReasons(file: File, matter: DiscoveryMatterProfile, remainingTotalBudget: number): Promise<DiscoveryMatchReason[]> {
  if (file.size > MAX_TEXT_SCAN_BYTES || file.size > remainingTotalBudget) {
    return [];
  }
  try {
    return contentTextReasons(await readFileAsText(file), matter);
  } catch {
    return [];
  }
}

/** A parse that hangs is as much a scan-availability risk as one that throws -- races the real parse against a plain timeout and treats either outcome as "this file contributed nothing," never as a fatal error for the whole scan (founder review comment 5729176495's "bounded per-file failure/size/time behavior"). */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Parse timed out')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** How long a single PDF/EML parse may run before it's abandoned and skipped, per founder review comment 5729176495's explicit "time budget, not just byte budget" requirement. */
export const PARSE_TIMEOUT_MS = 8000;

/** `.pdf` only -- approved for text-layer extraction by founder review comment 5728673302. No DOCX/XLSX/MSG/OCR/archive parser in this sub-slice (those stay in the §11/§12 proposal, unimplemented). */
const PDF_EXTENSION = '.pdf';
function isPdfCandidate(fileName: string): boolean {
  return fileName.toLowerCase().endsWith(PDF_EXTENSION);
}

/** Per-file cap on a PDF's own (compressed, on-disk) size before this feature will even attempt to open it -- an "individual file size" scan limit distinct from the parse-time timeout below, so a huge PDF is skipped outright rather than attempted and then timed out. */
export const MAX_PDF_SCAN_BYTES = 20_000_000;

/** Cumulative cap across one scan on how many PDF bytes get parsed -- the "total work" limit for this format, independent of TXT/CSV's own budget. */
export const MAX_TOTAL_PDF_SCAN_BYTES = 100_000_000;

let pdfjsModulePromise: Promise<typeof import('pdfjs-dist')> | null = null;

/**
 * Loaded via dynamic `import()` only the first time a `.pdf` candidate is actually encountered
 * mid-scan (founder review comment 5728673302's "load with dynamic import() only when that
 * format is actually encountered") -- `pdfjs-dist` and its worker script never become part of
 * this app's main bundle for the large majority of visitors who never use discovery on a PDF at
 * all. The worker script is loaded the same way, as a URL Vite resolves at build time, not
 * inlined into this module.
 */
async function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsModulePromise) {
    pdfjsModulePromise = (async () => {
      const [pdfjsLib, workerUrlModule] = await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.mjs?url')]);
      pdfjsLib.GlobalWorkerOptions.workerSrc = (workerUrlModule as {default: string}).default;
      return pdfjsLib;
    })().catch((error: unknown) => {
      // A transient failure (e.g. one chunk load blip) shouldn't permanently disable PDF
      // discovery for the rest of this browser tab's session -- only a successful load is cached.
      pdfjsModulePromise = null;
      throw error;
    });
  }
  return pdfjsModulePromise;
}

/**
 * Text-layer extraction only, per founder review comment 5728673302: `getDocument()` +
 * `page.getTextContent()` for every page, nothing else. This call path never renders the PDF,
 * never builds an annotation layer, and never constructs a `PDFScriptingManager` -- the
 * historical arbitrary-JS-execution CVEs in pdf.js (CVE-2026-16633, CVE-2024-4367) both require
 * that separate, opt-in scripting pipeline, which this function never touches or wires up.
 * Verified directly against this exact installed version's own type declarations (not assumed):
 * `isEvalSupported`/`enableScripting` are not `getDocument()` options in `pdfjs-dist` 6.3.289 at
 * all -- scripting is inert here structurally, not merely disabled by a flag that could be
 * accidentally left on. `disableAutoFetch`/`disableStream` stop pdf.js from opportunistically
 * fetching more of the document than this synchronous, one-shot text extraction needs.
 */
async function extractPdfText(file: File): Promise<string> {
  const pdfjsLib = await loadPdfjs();
  const data = new Uint8Array(await readFileAsArrayBuffer(file));
  const loadingTask = pdfjsLib.getDocument({data, disableAutoFetch: true, disableStream: true});
  try {
    const doc = await loadingTask.promise;
    const pageTexts: string[] = [];
    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
      const page = await doc.getPage(pageNumber);
      const content = await page.getTextContent();
      pageTexts.push(content.items.map((item) => ('str' in item ? item.str : '')).join(' '));
    }
    return pageTexts.join('\n');
  } finally {
    await loadingTask.destroy();
  }
}

/** Same shape as textContentReasons() above, for PDF text-layer content. Never throws. */
async function pdfContentReasons(file: File, matter: DiscoveryMatterProfile, remainingTotalBudget: number): Promise<DiscoveryMatchReason[]> {
  if (file.size > MAX_PDF_SCAN_BYTES || file.size > remainingTotalBudget) {
    return [];
  }
  try {
    return contentTextReasons(await withTimeout(extractPdfText(file), PARSE_TIMEOUT_MS), matter);
  } catch {
    return [];
  }
}

/** `.eml` only -- approved by founder review comment 5728673302. MSG (Outlook's proprietary binary format) is not attempted. */
const EML_EXTENSION = '.eml';
function isEmlCandidate(fileName: string): boolean {
  return fileName.toLowerCase().endsWith(EML_EXTENSION);
}

/** Per-file cap on a raw .eml file's own size before this feature will attempt to parse it. */
export const MAX_EML_SCAN_BYTES = MAX_EML_PARSE_BYTES;

/** Cumulative cap across one scan on how many EML bytes get parsed -- this format's own "total work" limit. */
export const MAX_TOTAL_EML_SCAN_BYTES = 25_000_000;

/**
 * Text/header extraction only, per founder review comment 5728673302: reads only `subject` and
 * `text` from the parsed message. `html` is never read (so message HTML is never rendered by
 * this feature, and never can be, since nothing here even looks at it), attachments are never
 * opened (the default `rfc822Attachments`/`forceRfc822Attachments: false` is left as-is, and this
 * function never touches `message.attachments` at all), and postal-mime itself performs no
 * network I/O of its own, so no remote resource is ever fetched while parsing an email.
 */
async function extractEmlText(file: File): Promise<string> {
  const buffer = await readFileAsArrayBuffer(file);
  return emlSearchText(await extractEmlPlainText(buffer));
}

/** Same shape as textContentReasons() above, for EML subject/text content. Never throws. */
async function emlContentReasons(file: File, matter: DiscoveryMatterProfile, remainingTotalBudget: number): Promise<DiscoveryMatchReason[]> {
  if (file.size > MAX_EML_SCAN_BYTES || file.size > remainingTotalBudget) {
    return [];
  }
  try {
    return contentTextReasons(await withTimeout(extractEmlText(file), PARSE_TIMEOUT_MS), matter);
  } catch {
    return [];
  }
}

/** `.docx` only -- approved by founder review comment 5730113935, gated on the ZIP archive preflight (fenzsafeArchivePreflight.ts) passing first. XLSX remains deferred per that same review: the archive preflight is necessary but not by itself sufficient for XLSX, which still needs a separately verified read-only parser. */
const DOCX_EXTENSION = '.docx';
function isDocxCandidate(fileName: string): boolean {
  return fileName.toLowerCase().endsWith(DOCX_EXTENSION);
}

/** Cap on a DOCX's own raw (compressed, on-disk) size before this feature will even read it into memory for the archive preflight -- "cap compressed file bytes before preflight" per the founder review. */
export const MAX_DOCX_SCAN_BYTES = 20_000_000;

/** Cumulative cap across one scan on how many DOCX bytes get parsed -- this format's own "total work" limit. */
export const MAX_TOTAL_DOCX_SCAN_BYTES = 100_000_000;

/**
 * Same shape as textContentReasons()/pdfContentReasons() above, for DOCX content -- with one
 * extra, mandatory step first: the ZIP archive preflight (fenzsafeArchivePreflight.ts) must pass
 * before `mammoth` (and the `JSZip` it uses internally) ever sees the file's bytes. A rejected
 * preflight contributes no reason, exactly like a parse failure -- it is a rejection layer, not a
 * proof of safety, so a file that passes still goes through the same timeout as every other
 * format. Never throws.
 */
async function docxContentReasons(file: File, matter: DiscoveryMatterProfile, remainingTotalBudget: number): Promise<DiscoveryMatchReason[]> {
  if (file.size > MAX_DOCX_SCAN_BYTES || file.size > remainingTotalBudget) {
    return [];
  }
  try {
    const buffer = await readFileAsArrayBuffer(file);
    return contentTextReasons(await withTimeout(extractDocxRawText(buffer), PARSE_TIMEOUT_MS), matter);
  } catch {
    return [];
  }
}

/**
 * A sane default scan limit (contract §7's "scan-induced DoS" mitigation) -- a very large
 * directory tree stops the scan rather than running unbounded, and `truncated` tells the caller
 * to say so rather than silently returning a partial result.
 */
export const MAX_FILES_EXAMINED = 5000;

export type DiscoveryScanResult = {
  candidates: DiscoveryCandidate[];
  filesExamined: number;
  truncated: boolean;
  /** True only when the caller's AbortSignal fired mid-scan (the user clicked "Cancel search") -- distinct from `truncated`, which means the scan itself hit its own file-count limit. */
  cancelled: boolean;
  /**
   * Local-only scan metrics (founder review comment 5729176495) -- computed and returned for the
   * UI to show the person who ran the search, never sent anywhere over the network by this
   * module or any caller. Carries only counts/durations, never a filename, path, file content, or
   * anything else about a specific candidate.
   */
  stats: DiscoveryScanStats;
};

export type DiscoveryScanStats = {
  elapsedMs: number;
  filesExamined: number;
  candidatesSurfaced: number;
  /** Milliseconds from scan start to the first candidate found, or null if none were found before the scan ended. */
  timeToFirstCandidateMs: number | null;
};

/**
 * Walks the picked directory tree, matching every file it encounters against the matter's
 * profile by filename (matchCandidate()), for TXT/CSV files within the text-scan size budgets by
 * content too (textContentReasons()), and by whether the file's own last-modified date falls
 * within the matter's own known-dates window (matterDateWindow()/dateWindowReason()). A file with
 * no match reason at all is never added to `candidates` -- this surfaces likely-related files,
 * not a full listing of everything in the picked folder (matching the product requirement's own
 * "candidates," not "every file"). Only reads a file's bytes/metadata (`getFile()`) for a file
 * that could possibly end up a candidate by some path (already matched by name, a text-scan-
 * eligible extension, or the matter has a usable date window to check it against); a huge folder
 * full of files that can't match any of those ways never pays that cost. Pass `signal` (an
 * AbortController's signal) to let the user cancel mid-scan; a cancelled scan returns whatever
 * candidates were already found, not an error.
 */
export async function scanDirectoryForCandidates(
  root: FileSystemDirectoryHandle,
  matter: DiscoveryMatterProfile,
  onProgress?: (filesExamined: number) => void,
  signal?: AbortSignal,
): Promise<DiscoveryScanResult> {
  const startedAt = Date.now();
  const dateWindow = matterDateWindow(matter);
  const candidates: DiscoveryCandidate[] = [];
  let filesExamined = 0;
  let truncated = false;
  let cancelled = false;
  let totalTextBytesRead = 0;
  let totalPdfBytesRead = 0;
  let totalEmlBytesRead = 0;
  let totalDocxBytesRead = 0;
  let timeToFirstCandidateMs: number | null = null;

  async function walk(directory: FileSystemDirectoryHandle, path: string): Promise<void> {
    for await (const [name, handle] of directory.entries()) {
      if (truncated || cancelled) {
        return;
      }
      if (signal?.aborted) {
        cancelled = true;
        return;
      }
      const entryPath = path === '' ? name : `${path}/${name}`;
      if (handle.kind === 'file') {
        filesExamined += 1;
        onProgress?.(filesExamined);
        if (filesExamined > MAX_FILES_EXAMINED) {
          truncated = true;
          return;
        }
        const nameMatch = matchCandidate(name, matter);
        const reasons: DiscoveryMatchReason[] = nameMatch ? [...nameMatch.reasons] : [];
        const needsFile = nameMatch !== null || isTextScanCandidate(name) || isPdfCandidate(name) || isEmlCandidate(name) || isDocxCandidate(name) || dateWindow !== null;
        if (needsFile) {
          const file = await handle.getFile();
          if (isTextScanCandidate(name)) {
            const remainingBudget = MAX_TOTAL_TEXT_SCAN_BYTES - totalTextBytesRead;
            if (remainingBudget > 0) {
              const contentReasons = await textContentReasons(file, matter, remainingBudget);
              if (contentReasons.length > 0) {
                totalTextBytesRead += file.size;
              }
              reasons.push(...contentReasons);
            }
          }
          if (isPdfCandidate(name)) {
            const remainingBudget = MAX_TOTAL_PDF_SCAN_BYTES - totalPdfBytesRead;
            if (remainingBudget > 0) {
              const contentReasons = await pdfContentReasons(file, matter, remainingBudget);
              if (contentReasons.length > 0) {
                totalPdfBytesRead += file.size;
              }
              reasons.push(...contentReasons);
            }
          }
          if (isEmlCandidate(name)) {
            const remainingBudget = MAX_TOTAL_EML_SCAN_BYTES - totalEmlBytesRead;
            if (remainingBudget > 0) {
              const contentReasons = await emlContentReasons(file, matter, remainingBudget);
              if (contentReasons.length > 0) {
                totalEmlBytesRead += file.size;
              }
              reasons.push(...contentReasons);
            }
          }
          if (isDocxCandidate(name)) {
            const remainingBudget = MAX_TOTAL_DOCX_SCAN_BYTES - totalDocxBytesRead;
            if (remainingBudget > 0) {
              const contentReasons = await docxContentReasons(file, matter, remainingBudget);
              if (contentReasons.length > 0) {
                totalDocxBytesRead += file.size;
              }
              reasons.push(...contentReasons);
            }
          }
          if (dateWindow) {
            const dateReason = dateWindowReason(file.lastModified, dateWindow);
            if (dateReason) reasons.push(dateReason);
          }
          if (reasons.length > 0) {
            if (timeToFirstCandidateMs === null) {
              timeToFirstCandidateMs = Date.now() - startedAt;
            }
            candidates.push({
              id: entryPath,
              name,
              path: entryPath,
              sizeBytes: file.size,
              lastModifiedAt: file.lastModified,
              reasons,
              band: bandFor(reasons),
              file,
            });
          }
        }
      } else {
        await walk(handle, entryPath);
      }
    }
  }

  await walk(root, '');
  candidates.sort((a, b) => {
    if (a.band !== b.band) {
      return a.band === 'strong' ? -1 : 1;
    }
    return b.reasons.length - a.reasons.length;
  });

  return {
    candidates,
    filesExamined,
    truncated,
    cancelled,
    stats: {elapsedMs: Date.now() - startedAt, filesExamined, candidatesSurfaced: candidates.length, timeToFirstCandidateMs},
  };
}
